/**
 * 用途：广告创建向导的共享基础组件（FormGroup、步骤条、滚动容器等），供各平台向导页复用
 * 路由：无（共享工具模块，不直接挂载路由）
 */
import React, { useEffect, useRef, useState } from 'react';

export type WizardStep = 1 | 2 | 3;

export function FormGroup({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div className="form-group" style={style}>{children}</div>;
}

export function Label({ children, required, style }: { children: React.ReactNode; required?: boolean; style?: React.CSSProperties }) {
  return (
    <label className="form-label" style={style}>
      {children}
      {required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}
    </label>
  );
}

export function Hint({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div className="form-hint" style={style}>{children}</div>;
}

export function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 10,
      marginBottom: 18,
    }}>
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
        fontSize: 12,
        fontWeight: 700,
        color: 'var(--text3)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: 'var(--surface)',
        borderRadius: '10px 10px 0 0',
      }}>
        {title}
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

export function UploadZone({
  onFile,
  onFiles,
  accept,
  hint,
  fileName,
  uploading,
  uploadPct,
  previewURL,
  multiple,
  disabled,
  actionSlot,
  onClear,
}: {
  onFile?: (f: File) => void;
  onFiles?: (files: File[]) => void;
  accept: string;
  hint: string;
  fileName?: string;
  uploading?: boolean;
  uploadPct?: number;
  previewURL?: string;
  multiple?: boolean;
  disabled?: boolean;
  actionSlot?: React.ReactNode;
  onClear?: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const isImage = accept.startsWith('image');
  const hasVideoPreview = Boolean(previewURL && accept.startsWith('video'));
  const emitFiles = (files: File[]) => {
    if (files.length === 0) return;
    if (multiple && onFiles) {
      onFiles(files);
      return;
    }
    if (onFile) onFile(files[0]);
  };

  return (
    <div>
      <div
        className="upload-zone"
        style={
          hasVideoPreview
            ? { padding: 0, border: '1px solid var(--green)', background: 'transparent', overflow: 'hidden', cursor: 'default', opacity: disabled ? 0.6 : 1 }
            : previewURL
            ? { padding: 0, border: '1px solid var(--green)', background: 'transparent', overflow: 'hidden', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }
            : fileName
              ? { borderColor: 'var(--green)', background: 'rgba(16,185,129,.04)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }
              : { cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }
        }
        onClick={() => {
          if (hasVideoPreview) return;
          if (!disabled && !uploading) ref.current?.click();
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const files = Array.from(e.dataTransfer.files || []);
          if (!disabled && !uploading) emitFiles(files);
        }}
      >
        {uploading ? (
          <div style={{ padding: '16px 20px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
              <span>{accept.startsWith('video') ? '视频上传中，请勿关闭...' : '上传中...'}</span>
              <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{uploadPct ?? 0}%</span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${uploadPct ?? 0}%`, background: 'var(--primary)', borderRadius: 3, transition: 'width 0.3s ease' }} />
            </div>
            {fileName && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>{fileName}</div>}
          </div>
        ) : previewURL && isImage ? (
          <div style={{ position: 'relative' }}>
            <img src={previewURL} alt="preview" style={{ display: 'block', width: '100%', maxHeight: 200, objectFit: 'contain', background: '#000' }} />
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(0,0,0,.55)',
                color: '#fff',
                fontSize: 11,
                padding: '6px 8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <span>{fileName || '素材已选择'}</span>
              {!disabled && (
                <>
                  <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => ref.current?.click()}>
                    替换
                  </button>
                  {onClear && (
                    <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '2px 8px', color: 'var(--red)' }} onClick={onClear}>
                      删除
                    </button>
                  )}
                  {actionSlot}
                </>
              )}
            </div>
          </div>
        ) : hasVideoPreview ? (
          <div
            style={{
              width: '100%',
              padding: 12,
              display: 'grid',
              gridTemplateColumns: '116px minmax(0, 1fr)',
              gap: 14,
              alignItems: 'start',
              background: 'var(--surface)',
            }}
          >
            <div style={{ borderRadius: 10, overflow: 'hidden', background: '#E8EAF0', aspectRatio: '9 / 16' }}>
              <video
                src={previewURL}
                controls
                playsInline
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ minWidth: 0, display: 'grid', gap: 8 }}>
              {fileName ? <div className="uz-text" style={{ color: 'var(--text1)', fontWeight: 700 }}>{fileName}</div> : null}
              <div className="uz-hint">视频已选择，可直接预览</div>
              {actionSlot ? <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actionSlot}</div> : null}
            </div>
          </div>
        ) : fileName ? (
          <>
            <div className="uz-icon" style={{ color: 'var(--green)' }}>✓</div>
            <div className="uz-text" style={{ color: 'var(--green)', fontWeight: 600 }}>{fileName}</div>
            <div className="uz-hint">点击重新选择</div>
            {!disabled && onClear && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                style={{ marginTop: 8, color: 'var(--red)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                删除
              </button>
            )}
          </>
        ) : (
          <>
            <div className="uz-icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div className="uz-text">点击选择素材</div>
            <div className="uz-hint">{hint}</div>
          </>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          emitFiles(files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

const MONTH_NAMES = ['1\u6708', '2\u6708', '3\u6708', '4\u6708', '5\u6708', '6\u6708', '7\u6708', '8\u6708', '9\u6708', '10\u6708', '11\u6708', '12\u6708'];
const DOW_LABELS = ['\u4e00', '\u4e8c', '\u4e09', '\u56db', '\u4e94', '\u516d', '\u65e5'];

export function DateTimePicker({
  value,
  onChange,
  placeholder = '选择日期时间',
  disabled = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const today = new Date();

  const selYear = value ? +value.slice(0, 4) : 0;
  const selMonth = value ? +value.slice(5, 7) - 1 : -1;
  const selDay = value ? +value.slice(8, 10) : 0;

  const [viewYear, setViewYear] = useState(() => selYear || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (selMonth >= 0 ? selMonth : today.getMonth()));
  const [hour, setHour] = useState(() => (value ? value.slice(11, 13) : '00'));
  const [minute, setMinute] = useState(() => (value ? value.slice(14, 16) : '00'));

  useEffect(() => {
    if (value) {
      setViewYear(+value.slice(0, 4));
      setViewMonth(+value.slice(5, 7) - 1);
      setHour(value.slice(11, 13));
      setMinute(value.slice(14, 16));
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const buildValue = (y: number, mo: number, d: number, h: string, mi: string) =>
    `${y}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}T${h}:${mi}`;

  const selectDay = (day: number) => onChange(buildValue(viewYear, viewMonth, day, hour, minute));

  const applyTime = (h: string, m: string) => {
    const base = value
      ? value.slice(0, 10)
      : `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    onChange(`${base}T${h}:${m}`);
  };

  const clampHour = (v: string) => String(Math.max(0, Math.min(23, +v || 0))).padStart(2, '0');
  const clampMinute = (v: string) => String(Math.max(0, Math.min(59, +v || 0))).padStart(2, '0');

  const firstDOW = new Date(viewYear, viewMonth, 1).getDay();
  const startPad = (firstDOW + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(startPad).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const isToday = (d: number) => d === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
  const isSelected = (d: number) => d === selDay && viewMonth === selMonth && viewYear === selYear;
  const display = value ? `${value.slice(0, 10)}  ${value.slice(11, 16)}` : '';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        className="form-input"
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none', gap: 8, minHeight: 36, opacity: disabled ? 0.6 : 1 }}
      >
        <span style={{ fontSize: 13, color: display ? 'var(--text)' : 'var(--text3)', letterSpacing: display ? '0.03em' : 0 }}>
          {display || placeholder}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, color: 'var(--text3)' }}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 2000,
            background: 'var(--surface)',
            border: '1px solid var(--border2)',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(0,0,0,.35)',
            padding: 14,
            width: 256,
            minWidth: 256,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button type="button" onClick={prevMonth} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--text2)', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{viewYear}年 {MONTH_NAMES[viewMonth]}</span>
            <button type="button" onClick={nextMonth} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--text2)', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
            {DOW_LABELS.map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'var(--text3)', fontWeight: 600, padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((day, i) => (
              <button
                key={i}
                type="button"
                disabled={!day}
                onClick={() => day && selectDay(day)}
                style={{
                  height: 30,
                  border: 'none',
                  borderRadius: 6,
                  cursor: day ? 'pointer' : 'default',
                  fontSize: 12,
                  fontWeight: day && isSelected(day) ? 700 : 400,
                  background: day && isSelected(day)
                    ? 'var(--primary)'
                    : day && isToday(day)
                      ? 'rgba(18,16,184,.12)'
                      : 'transparent',
                  color: day && isSelected(day)
                    ? '#fff'
                    : day && isToday(day)
                      ? 'var(--primary-light)'
                      : day
                        ? 'var(--text)'
                        : 'transparent',
                  transition: 'background .12s',
                }}
              >
                {day ?? ''}
              </button>
            ))}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', marginTop: 10, paddingTop: 10, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <input
              type="number"
              min={0}
              max={23}
              value={hour}
              onChange={(e) => {
                const h = clampHour(e.target.value);
                setHour(h);
                applyTime(h, minute);
              }}
              style={{ width: 44, textAlign: 'center', border: '1px solid var(--border2)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 15, fontWeight: 700, padding: '5px 0' }}
            />
            <span style={{ color: 'var(--text2)', fontWeight: 700, fontSize: 18, lineHeight: 1 }}>:</span>
            <input
              type="number"
              min={0}
              max={59}
              value={minute}
              onChange={(e) => {
                const m = clampMinute(e.target.value);
                setMinute(m);
                applyTime(hour, m);
              }}
              style={{ width: 44, textAlign: 'center', border: '1px solid var(--border2)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 15, fontWeight: 700, padding: '5px 0' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                style={{ flex: 1, padding: '7px 0', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}
              >
                清除
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ flex: 2, padding: '7px 0', background: 'var(--primary)', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600 }}
            >
              确定
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function HorizontalStepBar({ step, minStep }: { step: WizardStep; minStep?: WizardStep }) {
  const steps = [
    { n: 1 as WizardStep, label: '广告系列' },
    { n: 2 as WizardStep, label: '广告组' },
    { n: 3 as WizardStep, label: '广告创意' },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
      {steps.map((item, index) => {
        const lockedDone = minStep != null && item.n < minStep;
        const done = lockedDone || step > item.n;
        const active = !lockedDone && step === item.n;
        return (
          <React.Fragment key={item.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                background: done ? 'var(--green)' : active ? 'var(--primary)' : 'var(--surface3)',
                color: done || active ? '#fff' : 'var(--text3)',
                transition: 'all .2s',
              }}>
                {done ? '✓' : item.n}
              </div>
              <div style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? 'var(--text)' : done ? 'var(--green)' : 'var(--text3)' }}>
                {item.label}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: step > item.n ? 'var(--green)' : 'var(--border)', margin: '0 12px', transition: 'background .3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function WizardErrorBanner({
  title,
  message,
  hint,
  code,
  bannerRef,
}: {
  title: string;
  message: string;
  hint?: string;
  code?: string;
  bannerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div ref={bannerRef} style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(239,68,68,.4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'rgba(239,68,68,.12)' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>{title}</span>
        {code && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
            {code}
          </span>
        )}
      </div>
      <div style={{ padding: '8px 14px 4px', fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
        {message}
      </div>
      {hint && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '6px 14px 10px', fontSize: 12, color: '#d97706' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{hint}</span>
        </div>
      )}
    </div>
  );
}
